import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRightLeft, FolderKanban, Plus, MoreHorizontal, Archive, ArchiveRestore, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useConfirm } from "@/components/ConfirmDialog";
import { MoveDialog } from "@/components/MoveDialog";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";

function CreateProjectDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", defaultUrl: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleOpenChange = (next) => {
    if (!next) setForm({ name: "", description: "", defaultUrl: "" });
    onOpenChange(next);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/projects", {
        name: form.name.trim(),
        description: form.description.trim() || null,
        default_target_url: form.defaultUrl.trim() || null,
      });
      toast.success("Project created");
      onCreated?.(data);
      handleOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px] rounded-xl border-[#E5E7EB]" data-testid="create-project-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">New project</DialogTitle>
          <DialogDescription>Organise your tests by product, environment or team.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Project name</label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Customer Portal"
              data-testid="project-name-input"
              className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
            />
            <p className="text-xs text-[#64748B] mt-1">Use a recognisable name — typically your product, application or team.</p>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Description (optional)</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="What this project covers…"
              rows={3}
              data-testid="project-description-input"
              className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Default target URL (optional)</label>
            <Input
              value={form.defaultUrl}
              onChange={(e) => setForm((f) => ({ ...f, defaultUrl: e.target.value }))}
              placeholder="https://app.example.com"
              data-testid="project-url-input"
              className="rounded-md border-[#E5E7EB] font-mono text-xs focus-visible:ring-[#3B82F6]"
            />
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => handleOpenChange(false)}
            className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || !form.name.trim()}
            data-testid="create-project-submit"
            className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 flex items-center gap-1.5"
          >
            {submitting && <Loader2 size={13} className="animate-spin" />}
            Create project
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { current } = useWorkspace();
  const canMove = current?.is_personal === true && (current?.my_role === "owner" || current?.my_role === "editor");

  const load = useCallback(() => {
    api
      .get(`/projects${includeArchived ? "?include_archived=true" : ""}`)
      .then((r) => setProjects(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [includeArchived]);

  useEffect(() => { load(); }, [load]);

  const handleArchive = async (p) => {
    const ok = await confirm({
      title: p.archived ? "Restore this project?" : "Archive this project?",
      description: p.archived
        ? `"${p.name}" will be restored and visible again.`
        : `"${p.name}" will be hidden from the active list. You can restore it any time.`,
      confirmLabel: p.archived ? "Restore" : "Archive",
      testId: "confirm-archive-project",
    });
    if (!ok) return;
    try {
      await api.post(`/projects/${p.id}/${p.archived ? "unarchive" : "archive"}`);
      toast.success(p.archived ? "Project restored" : "Project archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const columns = [
    {
      key: "name",
      label: "Project",
      mobileVariant: "title",
      render: (p) => (
        <div className="flex flex-col">
          <Link
            to={`/projects/${p.id}`}
            data-testid={`project-link-${p.id}`}
            className="rt-primary hover:text-[#3B82F6] transition-colors"
          >
            {p.name}
            {p.archived && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#94A3B8]">Archived</span>}
          </Link>
          {p.description && (
            <span className="text-xs text-[#64748B] mt-0.5 line-clamp-1">{p.description}</span>
          )}
        </div>
      ),
    },
    {
      key: "target",
      label: "Default URL",
      render: (p) => p.default_target_url ? (
        <span className="rt-value font-mono text-xs text-[#64748B] inline-flex items-center gap-1 truncate">
          {p.default_target_url.replace(/^https?:\/\//, "").slice(0, 32)}
          <ExternalLink size={10} />
        </span>
      ) : <span className="text-xs text-[#94A3B8]">—</span>,
    },
    { key: "tests", label: "Tests", render: (p) => <span className="text-[#334155]">{p.test_count}</span> },
    { key: "suites", label: "Suites", render: (p) => <span className="text-[#334155]">{p.suite_count}</span> },
    {
      key: "last_run",
      label: "Last run",
      render: (p) => p.last_run
        ? <StatusBadge status={p.last_run.status} />
        : <span className="text-xs text-[#94A3B8]">No runs yet</span>,
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (p) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => navigate(`/projects/${p.id}`)}
            className="inline-flex items-center px-3 h-8 rounded-md border border-[#E5E7EB] bg-white text-sm font-medium text-[#0F172A] hover:bg-[#F8FAFC] transition-colors"
            data-testid={`open-project-${p.id}`}
          >
            Open
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`project-row-actions-${p.id}`}
                aria-label="Row actions"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8FAFC]"
              >
                <MoreHorizontal size={14} className="text-[#64748B]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-lg border-[#E5E7EB] p-1 w-44">
              {canMove && !p.archived && (
                <DropdownMenuItem
                  onClick={() => setMoveTarget(p)}
                  data-testid={`project-move-${p.id}`}
                  className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
                >
                  <ArrowRightLeft size={14} className="mr-2" />
                  Move to workspace…
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => handleArchive(p)}
                data-testid={`project-archive-${p.id}`}
                className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
              >
                {p.archived ? <ArchiveRestore size={14} className="mr-2" /> : <Archive size={14} className="mr-2" />}
                {p.archived ? "Restore" : "Archive"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="projects-page">
      <PageHeader
        title="Projects"
        description="Group your tests by product, app or team. Each project has its own suites, tests and run history."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIncludeArchived((v) => !v)}
              data-testid="toggle-archived-projects"
              className="inline-flex items-center px-3 h-10 rounded-md border border-[#E5E7EB] bg-white text-sm font-medium text-[#475569] hover:bg-[#F8FAFC] transition-colors"
            >
              {includeArchived ? "Hide archived" : "Show archived"}
            </button>
            <button
              onClick={() => setCreateOpen(true)}
              data-testid="new-project-button"
              className="inline-flex items-center gap-2 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors"
            >
              <Plus size={15} />
              New project
            </button>
          </div>
        }
      />

      {projects === null ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="empty-projects-message">
          <FolderKanban size={24} className="mx-auto mb-3 text-[#94A3B8]" />
          <h2 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">No projects yet</h2>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">
            Create your first project to start organising tests. Each project gets its own dashboard, suites, tests and run history.
          </p>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="empty-state-new-project-button"
            className="mt-6 inline-flex items-center gap-2 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors"
          >
            <Plus size={15} />
            Create your first project
          </button>
        </div>
      ) : (
        <ResponsiveTable
          testId="projects-table"
          columns={columns}
          rows={projects}
          getRowTestId={(p) => `project-row-${p.id}`}
        />
      )}

      <CreateProjectDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
      {moveTarget && (
        <MoveDialog
          open={!!moveTarget}
          onOpenChange={(o) => !o && setMoveTarget(null)}
          kind="project"
          entity={moveTarget}
          onMoved={load}
        />
      )}
    </div>
  );
}
